import { ProductDocsWidget } from '../../widgets/ProductDocsWidget';
import * as React from 'react';
import styled from '@emotion/styled';
import { CrumbWidget } from '../../widgets/CrumbWidget';
import { PageWidget } from '../../widgets/layout/PageWidget';
import { ContentWidget } from '../../widgets/ContentWidget';
import { SectionWidget } from '../../widgets/layout/SectionWidget';
import { Documents, ExternalLinks } from '../../RoutesWidget';

const image_ps = require('../../../media/stack/powersync-logo-white.png');
const image_modules = require('../../../media/stack/modules.png');
const image_hardware = require('../../../media/stack/img-smart-hardware-integration.png');
const image_desktop_devices = require('../../../media/desktop-mockup-devices-apps.png');
const image_responsive = require('../../../media/stack/responsive.jpg');
const image_debugger = require('../../../media/stack/debugger.png');
const image_whitepaper = require('../../../media/stack/resources-inside-spread-data-security-white-paper.png');
const image_device_agnostic = require('../../../media/stack/device-agnostic.png');

export const RuntimeEnginePage: React.FC = () => {
  return (
    <PageWidget width={1100} title="Runtime Engine">
      <ContentWidget>
        <h1>
          <CrumbWidget parts={['Platform stack', 'Runtime Engine']} />
        </h1>
        <p>
          The runtime engine is the heart of all apps built on the JourneyApps Platform. At its core, the engine is an
          advanced interpreter that renders the app, syncs and stores data, manages views and provides all the engines
          needed for an application to run on any device.
        </p>
        <S.ImageHero src={image_desktop_devices} />
        <SectionWidget
          image={image_ps}
          zoom={false}
          label={'PowerSync: Online / offline data sync engine'}
          content="PowerSync is at the heart of the engine and manages all application data syncing
        and allows the application to continue running even in an offline mode (no connectivity)."
          index={1}
        ></SectionWidget>
        <SectionWidget
          ltr={false}
          zoom={false}
          shadow={true}
          image={image_modules}
          label={'Modular, extensible design'}
          content="
        The latest version (V5) has been designed from the ground up to be modular and extensible by design. This allows developers
        to extend the engine, create their own extensions and take their applications to new heights of capability.
        "
          index={2}
        ></SectionWidget>
        <SectionWidget
          image={image_device_agnostic}
          zoom={false}
          shadow={true}
          label={'Device and operating system agnostic'}
          content="The runtime engine, much like a virtual machine and all popular game engines, ensures there is a common
          foundation to run applications regardless of device or operating system."
          index={3}
        ></SectionWidget>
        <h2>Version 5: A breakthrough in performance</h2>
        <p>
          In late 2025, JourneyApps released its major new version of the engine (V5) which had been developed over the
          span of 6 years. V5, along with new supporting technologies developed in-house, introduced 2x to 3x
          performance gains for almost all applications, especially apps with complex views.
        </p>
        <p>
          With V5 of the engine, applications built on the JourneyApps Platform are not only more performant than
          industry competitors, but are also much more deterministic and significantly less prone to failure.
        </p>
        <SectionWidget
          link={Documents.SECURITY}
          image={image_whitepaper}
          shadow={true}
          zoom={false}
          label={'Industry leading security'}
          content="Top global companies rely on the JourneyApps Platform to run their
businesses. JourneyApps Platform has comprehensive operational security controls and measures in place,
keeping our customers’ data and intellectual property safe. In addition, the JourneyApps Platform has been
designed with security at its core, ensuring that every app built on the platform automatically
maintains the same high security standards. All our Fortune 500 customers agree — JourneyApps
has passed each of their stringent security reviews, allowing them to deploy safe and robust
applications that work with sensitive information."
          index={1}
        ></SectionWidget>
        <SectionWidget
          ltr={false}
          zoom={false}
          image={image_responsive}
          link={ExternalLinks.DOCS_RESPONSIVE_APPS}
          label={'Build responsive, beautiful and capable apps'}
          content="Any application that you develop on the JourneyApps Platform will automatically be responsive and can therefore be used on either a
          smartphone, tablet, desktop or even a browser, without any additional development required.
          The JourneyApps Platform does however offer some special functionality aimed at making better use of the available
          real estate on the larger screen sizes. When you implement these features and run your app on a smartphone,
          the app will still work and the UI features will 'gracefully degrade' to match the smaller screen."
          index={2}
        ></SectionWidget>
        <SectionWidget
          zoom={false}
          shadow={false}
          image={image_hardware}
          label={'Hardware and sensor engine'}
          content={
            <>
              <p>
                Industrial-grade apps need hardware integrations — and we've got you covered. JourneyApps Platform comes
                with built-in with support for a variety of smart hardware, including:
              </p>
              <br></br>
              <ul>
                <li>
                  Reading and writing of RFID and NFC tags (for asset management and inventory management use cases)
                </li>
                <li>Ingesting readings from Bluetooth SPP and Bluetooth Low Energy (BLE) devices.</li>
                <li>Working with laser barcode scanners</li>
                <li>Working with data streams over TCP sockets</li>
                <li>Printing documents to portable thermal printers</li>
              </ul>
            </>
          }
          index={3}
        ></SectionWidget>
        <SectionWidget
          ltr={false}
          image={image_debugger}
          label={'Developer tools and introspection'}
          content={`The Runtime Engine is built on top of web technology which allows developers
          to work directly with all browser-based debuggers. The JourneyApps Platform further extends
          this capability with the ability to live-inspect view variables, place breakpoints on application code, and much more.
          Debugging application code has never been more intuitive and easy.`}
          index={1}
        ></SectionWidget>
      </ContentWidget>
      <ProductDocsWidget
        title="Build with the Runtime"
        description="Explore the components, APIs, and integrations that power your app."
        topics={['Runtime']}
      />
    </PageWidget>
  );
};
namespace S {
  export const Container = styled.div``;

  export const ImageHero = styled.img`
    box-shadow: none !important;
  `;
}
